/**
 * Math nodes — arithmetic, trigonometry, vector operations.
 */
import type { NodeBehaviour, PortValue } from '../types';
import { input, output } from '../node/NodeRegistry';

// ── Arithmetic ──────────────────────────────────────────────────

export const Add: NodeBehaviour = {
  id: 'math.add',
  name: 'Add',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Plus',
  description: 'A + B',
  keywords: ['plus', 'sum', 'addition'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 0),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) + (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('add', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Subtract: NodeBehaviour = {
  id: 'math.subtract',
  name: 'Subtract',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Minus',
  description: 'A - B',
  keywords: ['minus', 'difference'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 0),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) - (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('sub', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Multiply: NodeBehaviour = {
  id: 'math.multiply',
  name: 'Multiply',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'X',
  description: 'A × B',
  keywords: ['times', 'product', 'mul'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 0),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) * (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('mul', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Divide: NodeBehaviour = {
  id: 'math.divide',
  name: 'Divide',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Divide',
  description: 'A / B (returns 0 if B = 0)',
  keywords: ['ratio', 'div'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 1),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const b = ctx.getInput<number>('b') ?? 1;
    ctx.setOutput('result', b !== 0 ? (ctx.getInput<number>('a') ?? 0) / b : 0);
  },
  compile(ctx) {
    return ctx.emit('op:div', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Modulo: NodeBehaviour = {
  id: 'math.modulo',
  name: 'Modulo',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Percent',
  description: 'A % B',
  keywords: ['remainder', 'mod', 'wrap'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 1),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const b = ctx.getInput<number>('b') ?? 1;
    ctx.setOutput('result', b !== 0 ? (ctx.getInput<number>('a') ?? 0) % b : 0);
  },
  compile(ctx) {
    return ctx.emit('op:mod', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Negate: NodeBehaviour = {
  id: 'math.negate',
  name: 'Negate',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'MinusCircle',
  description: '-A',
  keywords: ['negative', 'flip', 'inverse'],
  inputs: [input('a', 'A', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', -(ctx.getInput<number>('a') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:neg', [ctx.resolveInput('a')], 'numeric');
  },
};

export const Abs: NodeBehaviour = {
  id: 'math.abs',
  name: 'Absolute',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Sigma',
  description: '|A|',
  keywords: ['absolute', 'magnitude'],
  inputs: [input('a', 'A', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', Math.abs(ctx.getInput<number>('a') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:abs', [ctx.resolveInput('a')], 'numeric');
  },
};

// ── Trigonometry ────────────────────────────────────────────────

export const Sin: NodeBehaviour = {
  id: 'math.sin',
  name: 'Sin',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'Activity',
  description: 'sin(A) (radians)',
  keywords: ['sine', 'trig'],
  inputs: [input('a', 'Angle', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) { ctx.setOutput('result', Math.sin(ctx.getInput<number>('a') ?? 0)); },
  compile(ctx) {
    return ctx.emit('op:sin', [ctx.resolveInput('a')], 'numeric');
  },
};

export const Cos: NodeBehaviour = {
  id: 'math.cos',
  name: 'Cos',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'Activity',
  description: 'cos(A) (radians)',
  keywords: ['cosine', 'trig'],
  inputs: [input('a', 'Angle', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) { ctx.setOutput('result', Math.cos(ctx.getInput<number>('a') ?? 0)); },
  compile(ctx) {
    return ctx.emit('op:cos', [ctx.resolveInput('a')], 'numeric');
  },
};

export const Atan2: NodeBehaviour = {
  id: 'math.atan2',
  name: 'Atan2',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'Compass',
  description: 'atan2(Y, X) → angle in radians',
  keywords: ['arctangent', 'angle', 'direction'],
  inputs: [
    input('y', 'Y', 'numeric', 0),
    input('x', 'X', 'numeric', 1),
  ],
  outputs: [output('result', 'Angle', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', Math.atan2(
      ctx.getInput<number>('y') ?? 0,
      ctx.getInput<number>('x') ?? 1,
    ));
  },
  compile(ctx) {
    return ctx.emit('op:atan2', [ctx.resolveInput('y'), ctx.resolveInput('x')], 'numeric');
  },
};

export const Sqrt: NodeBehaviour = {
  id: 'math.sqrt',
  name: 'Square Root',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'Radical',
  keywords: ['root'],
  inputs: [input('a', 'Value', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) { ctx.setOutput('result', Math.sqrt(Math.max(0, ctx.getInput<number>('a') ?? 0))); },
  compile(ctx) {
    return ctx.emit('op:sqrt', [ctx.resolveInput('a')], 'numeric');
  },
};

export const Power: NodeBehaviour = {
  id: 'math.pow',
  name: 'Power',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'Superscript',
  description: 'Base ^ Exponent',
  keywords: ['exponent'],
  inputs: [
    input('base', 'Base', 'numeric', 0),
    input('exp', 'Exponent', 'numeric', 2),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', Math.pow(ctx.getInput<number>('base') ?? 0, ctx.getInput<number>('exp') ?? 2));
  },
  compile(ctx) {
    return ctx.emit('op:pow', [ctx.resolveInput('base'), ctx.resolveInput('exp')], 'numeric');
  },
};

// ── Clamping / Interpolation ────────────────────────────────────

export const Clamp: NodeBehaviour = {
  id: 'math.clamp',
  name: 'Clamp',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Minimize2',
  description: 'Clamps value between min and max.',
  keywords: ['limit', 'range', 'constrain'],
  inputs: [
    input('value', 'Value', 'numeric', 0),
    input('min', 'Min', 'numeric', 0),
    input('max', 'Max', 'numeric', 1),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const v = ctx.getInput<number>('value') ?? 0;
    const lo = ctx.getInput<number>('min') ?? 0;
    const hi = ctx.getInput<number>('max') ?? 1;
    ctx.setOutput('result', Math.min(hi, Math.max(lo, v)));
  },
  compile(ctx) {
    return ctx.emit('op:clamp', [ctx.resolveInput('value'), ctx.resolveInput('min'), ctx.resolveInput('max')], 'numeric');
  },
};

export const Lerp: NodeBehaviour = {
  id: 'math.lerp',
  name: 'Lerp',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'Blend',
  description: 'Linear interpolation: A + (B - A) * T',
  keywords: ['interpolate', 'mix', 'blend'],
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 1),
    input('t', 'T', 'numeric', 0.5),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const a = ctx.getInput<number>('a') ?? 0;
    const b = ctx.getInput<number>('b') ?? 1;
    const t = ctx.getInput<number>('t') ?? 0.5;
    ctx.setOutput('result', a + (b - a) * t);
  },
  compile(ctx) {
    return ctx.emit('op:lerp', [ctx.resolveInput('a'), ctx.resolveInput('b'), ctx.resolveInput('t')], 'numeric');
  },
};

export const Min: NodeBehaviour = {
  id: 'math.min',
  name: 'Min',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'ArrowDown',
  description: 'Minimum of A and B',
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 0),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', Math.min(ctx.getInput<number>('a') ?? 0, ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:min', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

export const Max: NodeBehaviour = {
  id: 'math.max',
  name: 'Max',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'ArrowUp',
  description: 'Maximum of A and B',
  inputs: [
    input('a', 'A', 'numeric', 0),
    input('b', 'B', 'numeric', 0),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    ctx.setOutput('result', Math.max(ctx.getInput<number>('a') ?? 0, ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:max', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'numeric');
  },
};

// ── Random ──────────────────────────────────────────────────────

export const RandomFloat: NodeBehaviour = {
  id: 'math.random',
  name: 'Random',
  category: 'Math',
  isPure: true,
  color: '#e67e22',
  icon: 'Dices',
  description: 'Random value between Min and Max.',
  keywords: ['rand', 'noise'],
  inputs: [
    input('min', 'Min', 'numeric', 0),
    input('max', 'Max', 'numeric', 1),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const lo = ctx.getInput<number>('min') ?? 0;
    const hi = ctx.getInput<number>('max') ?? 1;
    ctx.setOutput('result', lo + Math.random() * (hi - lo));
  },
};

// ── Vector Math ─────────────────────────────────────────────────

export const MakeVec2: NodeBehaviour = {
  id: 'math.makeVec2',
  name: 'Make Vector 2',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'Axis3d',
  description: 'Creates a Vec2 from X and Y.',
  keywords: ['vector', 'point', 'construct'],
  inputs: [
    input('x', 'X', 'numeric', 0),
    input('y', 'Y', 'numeric', 0),
  ],
  outputs: [output('result', 'Vector', 'vec2')],
  execute(ctx) {
    ctx.setOutput('result', {
      x: ctx.getInput<number>('x') ?? 0,
      y: ctx.getInput<number>('y') ?? 0,
    });
  },
  compile(ctx) {
    return ctx.emit('op:vec2', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
};

export const BreakVec2: NodeBehaviour = {
  id: 'math.breakVec2',
  name: 'Break Vector 2',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'Ungroup',
  description: 'Extracts X and Y from a Vec2.',
  keywords: ['vector', 'decompose', 'split'],
  inputs: [
    input('vec', 'Vector', 'vec2'),
  ],
  outputs: [
    output('x', 'X', 'numeric'),
    output('y', 'Y', 'numeric'),
  ],
  execute(ctx) {
    const v = ctx.getInput<{ x: number; y: number }>('vec') ?? { x: 0, y: 0 };
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
  },
};

export const Vec2Distance: NodeBehaviour = {
  id: 'math.distance',
  name: 'Distance',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'Ruler',
  description: 'Distance between two Vec2 points.',
  keywords: ['length', 'between'],
  inputs: [
    input('a', 'A', 'vec2'),
    input('b', 'B', 'vec2'),
  ],
  outputs: [output('result', 'Distance', 'numeric')],
  execute(ctx) {
    const a = ctx.getInput<{ x: number; y: number }>('a') ?? { x: 0, y: 0 };
    const b = ctx.getInput<{ x: number; y: number }>('b') ?? { x: 0, y: 0 };
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    ctx.setOutput('result', Math.sqrt(dx * dx + dy * dy));
  },
};

export const Vec2Normalize: NodeBehaviour = {
  id: 'math.normalize',
  name: 'Normalize',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'Navigation',
  description: 'Normalize a Vec2 to unit length.',
  keywords: ['direction', 'unit'],
  inputs: [input('vec', 'Vector', 'vec2')],
  outputs: [output('result', 'Normalized', 'vec2')],
  execute(ctx) {
    const v = ctx.getInput<{ x: number; y: number }>('vec') ?? { x: 0, y: 0 };
    const len = Math.sqrt(v.x * v.x + v.y * v.y);
    if (len > 0.00001) {
      ctx.setOutput('result', { x: v.x / len, y: v.y / len });
    } else {
      ctx.setOutput('result', { x: 0, y: 0 });
    }
  },
};

export const Vec2Dot: NodeBehaviour = {
  id: 'math.dot',
  name: 'Dot Product',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'Target',
  description: 'Dot product of two Vec2.',
  inputs: [
    input('a', 'A', 'vec2'),
    input('b', 'B', 'vec2'),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const a = ctx.getInput<{ x: number; y: number }>('a') ?? { x: 0, y: 0 };
    const b = ctx.getInput<{ x: number; y: number }>('b') ?? { x: 0, y: 0 };
    ctx.setOutput('result', a.x * b.x + a.y * b.y);
  },
};

// ── Extra Math ──────────────────────────────────────────────────

export const Exp: NodeBehaviour = {
  id: 'math.exp',
  name: 'Exp',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'TrendingUp',
  description: 'e^A (natural exponential)',
  keywords: ['exponential', 'euler'],
  inputs: [input('a', 'Value', 'numeric', 0)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) { ctx.setOutput('result', Math.exp(ctx.getInput<number>('a') ?? 0)); },
};

export const Log: NodeBehaviour = {
  id: 'math.log',
  name: 'Log',
  category: 'Math',
  isPure: true,
  color: '#9b59b6',
  icon: 'TrendingDown',
  description: 'ln(A) (natural logarithm)',
  keywords: ['logarithm', 'natural'],
  inputs: [input('a', 'Value', 'numeric', 1)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) { ctx.setOutput('result', Math.log(Math.max(1e-10, ctx.getInput<number>('a') ?? 1))); },
};

export const Reciprocal: NodeBehaviour = {
  id: 'math.reciprocal',
  name: 'Reciprocal',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'FlipVertical',
  description: '1 / A',
  keywords: ['inverse', 'reciprocal'],
  inputs: [input('a', 'Value', 'numeric', 1)],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const a = ctx.getInput<number>('a') ?? 1;
    ctx.setOutput('result', a !== 0 ? 1 / a : 0);
  },
};

export const Length: NodeBehaviour = {
  id: 'math.length',
  name: 'Length',
  category: 'Math',
  isPure: true,
  color: '#1abc9c',
  icon: 'MoveRight',
  description: 'Magnitude (length) of a Vec2.',
  keywords: ['magnitude', 'norm', 'abs'],
  inputs: [input('vec', 'Vector', 'vec2')],
  outputs: [output('result', 'Length', 'numeric')],
  execute(ctx) {
    const v = ctx.getInput<{ x: number; y: number }>('vec') ?? { x: 0, y: 0 };
    ctx.setOutput('result', Math.sqrt(v.x * v.x + v.y * v.y));
  },
};

export const Posterize: NodeBehaviour = {
  id: 'math.posterize',
  name: 'Posterize',
  category: 'Math',
  isPure: true,
  color: '#3498db',
  icon: 'BarChart3',
  description: 'Quantizes a value to N steps.',
  keywords: ['quantize', 'snap', 'step'],
  inputs: [
    input('value', 'Value', 'numeric', 0),
    input('steps', 'Steps', 'i32', 4),
  ],
  outputs: [output('result', 'Result', 'numeric')],
  execute(ctx) {
    const v = ctx.getInput<number>('value') ?? 0;
    const s = Math.max(1, ctx.getInput<number>('steps') ?? 4);
    ctx.setOutput('result', Math.floor(v * s) / s);
  },
};

// ── Vec3 ────────────────────────────────────────────────────────

export const MakeVec3: NodeBehaviour = {
  id: 'math.makeVec3',
  name: 'Make Vector 3',
  category: 'Math',
  isPure: true,
  color: '#16a085',
  icon: 'Box',
  description: 'Creates a Vec3 from X, Y, Z.',
  keywords: ['vector', 'construct', 'compose', '3d'],
  inputs: [
    input('x', 'X', 'numeric', 0),
    input('y', 'Y', 'numeric', 0),
    input('z', 'Z', 'numeric', 0),
  ],
  outputs: [output('result', 'Vector', 'vec3')],
  execute(ctx) {
    ctx.setOutput('result', {
      x: ctx.getInput<number>('x') ?? 0,
      y: ctx.getInput<number>('y') ?? 0,
      z: ctx.getInput<number>('z') ?? 0,
    });
  },
};

export const BreakVec3: NodeBehaviour = {
  id: 'math.breakVec3',
  name: 'Break Vector 3',
  category: 'Math',
  isPure: true,
  color: '#16a085',
  icon: 'Ungroup',
  description: 'Extracts X, Y, Z from a Vec3.',
  keywords: ['vector', 'decompose', 'split', '3d'],
  inputs: [input('vec', 'Vector', 'vec3')],
  outputs: [
    output('x', 'X', 'numeric'),
    output('y', 'Y', 'numeric'),
    output('z', 'Z', 'numeric'),
  ],
  execute(ctx) {
    const v = ctx.getInput<{ x: number; y: number; z: number }>('vec') ?? { x: 0, y: 0, z: 0 };
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
    ctx.setOutput('z', v.z);
  },
};

// ── Vec4 ────────────────────────────────────────────────────────

export const MakeVec4: NodeBehaviour = {
  id: 'math.makeVec4',
  name: 'Make Vector 4',
  category: 'Math',
  isPure: true,
  color: '#0d8b70',
  icon: 'Box',
  description: 'Creates a Vec4 from X, Y, Z, W.',
  keywords: ['vector', 'construct', 'compose', '4d'],
  inputs: [
    input('x', 'X', 'numeric', 0),
    input('y', 'Y', 'numeric', 0),
    input('z', 'Z', 'numeric', 0),
    input('w', 'W', 'numeric', 0),
  ],
  outputs: [output('result', 'Vector', 'vec4')],
  execute(ctx) {
    ctx.setOutput('result', {
      x: ctx.getInput<number>('x') ?? 0,
      y: ctx.getInput<number>('y') ?? 0,
      z: ctx.getInput<number>('z') ?? 0,
      w: ctx.getInput<number>('w') ?? 0,
    });
  },
};

export const BreakVec4: NodeBehaviour = {
  id: 'math.breakVec4',
  name: 'Break Vector 4',
  category: 'Math',
  isPure: true,
  color: '#0d8b70',
  icon: 'Ungroup',
  description: 'Extracts X, Y, Z, W from a Vec4.',
  keywords: ['vector', 'decompose', 'split', '4d'],
  inputs: [input('vec', 'Vector', 'vec4')],
  outputs: [
    output('x', 'X', 'numeric'),
    output('y', 'Y', 'numeric'),
    output('z', 'Z', 'numeric'),
    output('w', 'W', 'numeric'),
  ],
  execute(ctx) {
    const v = ctx.getInput<{ x: number; y: number; z: number; w: number }>('vec') ?? { x: 0, y: 0, z: 0, w: 0 };
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
    ctx.setOutput('z', v.z);
    ctx.setOutput('w', v.w);
  },
};

// ── Quaternion ──────────────────────────────────────────────────

export const MakeQuat: NodeBehaviour = {
  id: 'math.makeQuat',
  name: 'Make Quaternion',
  category: 'Math',
  isPure: true,
  color: '#e056a0',
  icon: 'RotateCcw',
  description: 'Creates a quaternion from X, Y, Z, W.',
  keywords: ['quaternion', 'rotation', 'construct'],
  inputs: [
    input('x', 'X', 'numeric', 0),
    input('y', 'Y', 'numeric', 0),
    input('z', 'Z', 'numeric', 0),
    input('w', 'W', 'numeric', 1),
  ],
  outputs: [output('result', 'Quaternion', 'quat')],
  execute(ctx) {
    ctx.setOutput('result', {
      x: ctx.getInput<number>('x') ?? 0,
      y: ctx.getInput<number>('y') ?? 0,
      z: ctx.getInput<number>('z') ?? 0,
      w: ctx.getInput<number>('w') ?? 1,
    });
  },
};

export const BreakQuat: NodeBehaviour = {
  id: 'math.breakQuat',
  name: 'Break Quaternion',
  category: 'Math',
  isPure: true,
  color: '#e056a0',
  icon: 'Ungroup',
  description: 'Extracts X, Y, Z, W from a quaternion.',
  keywords: ['quaternion', 'decompose', 'split'],
  inputs: [input('quat', 'Quaternion', 'quat')],
  outputs: [
    output('x', 'X', 'numeric'),
    output('y', 'Y', 'numeric'),
    output('z', 'Z', 'numeric'),
    output('w', 'W', 'numeric'),
  ],
  execute(ctx) {
    const q = ctx.getInput<{ x: number; y: number; z: number; w: number }>('quat') ?? { x: 0, y: 0, z: 0, w: 1 };
    ctx.setOutput('x', q.x);
    ctx.setOutput('y', q.y);
    ctx.setOutput('z', q.z);
    ctx.setOutput('w', q.w);
  },
};

export const QuatFromEuler: NodeBehaviour = {
  id: 'math.quatFromEuler',
  name: 'Quat From Euler',
  category: 'Math',
  isPure: true,
  color: '#e056a0',
  icon: 'Rotate3d',
  description: 'Creates a quaternion from euler angles (radians, XYZ order).',
  keywords: ['quaternion', 'rotation', 'euler', 'angles'],
  inputs: [
    input('x', 'Pitch', 'numeric', 0),
    input('y', 'Yaw', 'numeric', 0),
    input('z', 'Roll', 'numeric', 0),
  ],
  outputs: [output('result', 'Quaternion', 'quat')],
  execute(ctx) {
    const px = (ctx.getInput<number>('x') ?? 0) * 0.5;
    const py = (ctx.getInput<number>('y') ?? 0) * 0.5;
    const pz = (ctx.getInput<number>('z') ?? 0) * 0.5;
    const sx = Math.sin(px), cx = Math.cos(px);
    const sy = Math.sin(py), cy = Math.cos(py);
    const sz = Math.sin(pz), cz = Math.cos(pz);
    ctx.setOutput('result', {
      x: sx * cy * cz - cx * sy * sz,
      y: cx * sy * cz + sx * cy * sz,
      z: cx * cy * sz - sx * sy * cz,
      w: cx * cy * cz + sx * sy * sz,
    });
  },
};

export const MATH_NODES: readonly NodeBehaviour[] = [
  Add, Subtract, Multiply, Divide, Modulo, Negate, Abs,
  Sin, Cos, Atan2, Sqrt, Power,
  Exp, Log, Reciprocal, Length, Posterize,
  Clamp, Lerp, Min, Max, RandomFloat,
  MakeVec2, BreakVec2, Vec2Distance, Vec2Normalize, Vec2Dot,
  MakeVec3, BreakVec3, MakeVec4, BreakVec4,
  MakeQuat, BreakQuat, QuatFromEuler,
];
